import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MasterLayout from "../Sections/MasterLayout";
import {
  server_post_json,
  update_order_status,
  get_order_detail,
  push_order_qikink,
  mark_order_paid,
} from "../../ServiceConnection/serviceconnection";

import toast from "react-hot-toast";

/* ================= ORDER FLOW ================= */
const STATUS_FLOW = ["pending", "shipped", "delivered", "cancelled"];

const STATUS_LABEL = {
  pending: "Pending",
  shipped: "Shipped",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const STATUS_STYLE = {
  completed: "bg-green-100 text-green-800 border-green-300",
  active: "bg-amber-100 text-amber-800 border-amber-300",
  next: "bg-blue-600 text-white hover:bg-blue-700",
  locked: "bg-gray-100 text-gray-400 cursor-not-allowed",
  cancelled: "bg-red-100 text-red-700 border-red-300",
};

const EMAIL_MAP = {
  pending: "Order Confirmed",
  sent_to_qikink: "Order In Production",
  shipped: "Order Shipped",
  delivered: "Order Delivered",
  cancelled: "Order Cancelled",
};

export default function OrderDetails() {
  const navigate = useNavigate();
  const { id: order_id } = useParams();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  const [showShippingForm, setShowShippingForm] = useState(false);
  const [trackingId, setTrackingId] = useState("");
  const [courier, setCourier] = useState("Pathmer");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [copiedSku, setCopiedSku] = useState(null);

  /**-------Couriers ----------*/
  const COURIERS = ["Pathmer", "Ekart", "Delhivery Air", "Blue Dart", "DTDC"];

  /* ================= FETCH ORDER (SOURCE OF TRUTH) ================= */
  const fetchOrderDetail = async () => {
    try {
      setLoading(true);
      const res = await server_post_json(get_order_detail, {
        order_id,
      });
      setOrder(res.data.order);
    } catch (err) {
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetail();
  }, [order_id]);

  if (!order) {
    return (
      <MasterLayout>
        <div className="p-6 text-center text-gray-500">
          Loading order details...
        </div>
      </MasterLayout>
    );
  }

  /* ================= STATUS TYPE LOGIC ================= */
  const getStatusType = (status) => {
    if (order.orderStatus === "cancelled") {
      return status === "cancelled" ? "cancelled" : "locked";
    }

    const currentIndex = STATUS_FLOW.indexOf(order.orderStatus);
    const statusIndex = STATUS_FLOW.indexOf(status);

    if (statusIndex < currentIndex) return "completed";
    if (statusIndex === currentIndex) return "active";
    if (statusIndex === currentIndex + 1) return "next";
    return "locked";
  };

  /* ================= UPDATE STATUS ================= */
  const handleStatusUpdate = async (status) => {
    if (status === order.orderStatus) return;

    // 🚚 SHIPPED → open input form
    if (status === "shipped") {
      setShowShippingForm(true);
      return;
    }

    try {
      setLoading(true);

      await server_post_json(update_order_status, {
        order_id: order.order_id,
        order_status: status,
      });

      toast.success(`Order marked as ${STATUS_LABEL[status]}`);
      fetchOrderDetail();
    } catch (err) {
      toast.error(err?.message || "Failed to update order status");
    } finally {
      setLoading(false);
    }
  };

  /* ================= PUSH TO QIKINK ================= */
  const handlePushToQikink = async () => {
    try {
      setLoading(true);

      await server_post_json(push_order_qikink, {
        order_id: order.order_id,
      });

      toast.success("Order pushed to Qikink successfully 🚀");

      // 🔥 Qikink updates DB → refetch
      fetchOrderDetail();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to push order to Qikink"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopySku = async (sku) => {
    try {
      await navigator.clipboard.writeText(sku);
      setCopiedSku(sku);

      setTimeout(() => {
        setCopiedSku(null);
      }, 2000);
    } catch (err) {
      toast.error("Failed to copy SKU");
    }
  };

  const handleMarkPaid = async () => {
    try {
      setLoading(true);

      await server_post_json(mark_order_paid, {
        order_id: order.order_id,
      });

      toast.success("Order marked as PAID 💰");
      fetchOrderDetail();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to mark order paid");
    } finally {
      setLoading(false);
    }
  };

  const handleShippingSubmit = async () => {
    if (!trackingId || !courier) {
      toast.error("Tracking ID and courier are required");
      return;
    }

    try {
      setLoading(true);

      await server_post_json(update_order_status, {
        order_id: order.order_id,
        order_status: "shipped",
        tracking: {
          tracking_id: trackingId,
          courier,
          tracking_url: trackingUrl || null,
        },
      });

      toast.success("Order marked as Shipped 🚚");

      setShowShippingForm(false);
      setTrackingId("");
      setTrackingUrl("");
      setCourier("Pathmer");

      fetchOrderDetail();
    } catch (err) {
      toast.error(err?.message || "Failed to update shipping");
    } finally {
      setLoading(false);
    }
  };

  // Enhanced: Get email history based on current status
  const getEmailHistory = () => {
    const currentIndex = STATUS_FLOW.indexOf(order.orderStatus);
    return STATUS_FLOW.slice(0, currentIndex + 1).filter(
      (status) => status !== "cancelled" || order.orderStatus === "cancelled"
    );
  };

  return (
    <MasterLayout>
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* ================= HEADER ================= */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold">Order #{order.order_id}</h2>
            <p className="text-sm text-gray-500">
              Placed on {new Date(order.createdAt).toLocaleString("en-IN")}
            </p>
            {order.updatedAt && (
              <p className="text-sm text-gray-500">
                Last Updated:{" "}
                {new Date(order.updatedAt).toLocaleString("en-IN")}
              </p>
            )}
          </div>

          <button
            onClick={() => navigate(-1)}
            className="text-blue-600 text-sm hover:underline"
          >
            ← Back
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ================= LEFT ================= */}
          <div className="lg:col-span-2 space-y-6">
            {/* ITEMS */}
            <div className="bg-white border rounded-xl p-5">
              <h3 className="font-semibold mb-4">Order Items</h3>

              <div className="flex gap-4">
                {order.items.map((item, i) => (
                  <div key={i} className="border rounded-lg p-3 flex flex-wrap gap-3">
                    <div className="w-20 h-20 border rounded bg-gray-50 flex items-center  justify-center">
                      {item.design_url ? (
                        <img
                          src={item.design_url}
                          className="w-full h-full object-contain"
                          alt=""
                        />
                      ) : (
                        <span className="text-xs text-gray-400">No Image</span>
                      )}
                    </div>

                    <div className="text-sm">
                      <p className="font-medium">{item.title}</p>
                      <p className="text-gray-500">
                        Size: {item.size} • Qty: {item.quantity}
                      </p>
                      <p className="text-gray-500 flex flex-wrap items-center gap-2 mt-1">
                        <span>SKU:</span>

                        <span className="font-bold text-black/80 text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {item.qikink_sku}
                        </span>

                        <button
                          onClick={() => handleCopySku(item.qikink_sku)}
                          className="text-xs px-2 py-0.5 rounded border hover:bg-gray-100"
                        >
                          {copiedSku === item.qikink_sku
                            ? "✅ Copied"
                            : "📋 Copy"}
                        </button>
                      </p>

                      <p className="font-semibold mt-1">₹{item.price}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Subtotal, Shipping, Total */}
              <div className="mt-6 border-t pt-4 text-sm">
                <div className="flex justify-between mb-1">
                  <span>Subtotal:</span>
                  <span>₹{order.subtotal}</span>
                </div>
                <div className="flex justify-between mb-1">
                  <span>Shipping Charge:</span>
                  <span>
                    {order.shippingCharge === 0
                      ? "FREE"
                      : `₹${order.shippingCharge}`}
                  </span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>Total:</span>
                  <span>₹{order.totalAmount}</span>
                </div>
              </div>
            </div>

            {/* CUSTOMER & SHIPPING DETAILS */}
            <div className="bg-white border rounded-xl p-5">
              <h3 className="font-semibold mb-4">
                Customer & Shipping Details
              </h3>

              <div className="text-sm space-y-4">
                <div>
                  <p className="font-medium">Customer</p>
                  <p>{order.shippingAddress.name}</p>
                  <p>{order.shippingAddress.phone}</p>
                </div>

                <div>
                  <p className="font-medium">Shipping Address</p>
                  <p>{order.shippingAddress.address}</p>
                  <p>
                    {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
                    {order.shippingAddress.pincode}
                  </p>
                  <p>{order.shippingAddress.country}</p>
                </div>

                <div>
                  <p className="font-medium">Payment</p>
                  <p>Method: {order.paymentMethod}</p>
                  <p>Status: {order.paymentStatus}</p>
                </div>
              </div>
            </div>

            {/* TRACKING INFO - Show if tracking exists (for shipped or delivered) */}
            {(order.orderStatus === "shipped" ||
              order.orderStatus === "delivered") &&
              order.tracking && (
                <div className="bg-white border rounded-xl p-5">
                  <h3 className="font-semibold mb-4">Tracking Information</h3>

                  <div className="text-sm space-y-2 border rounded-lg p-4">
                    <div className="flex justify-between">
                      <span>Tracking ID:</span>
                      <span className="font-medium">
                        {order.tracking.tracking_id}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Courier:</span>
                      <span className="font-medium">
                        {order.tracking.courier}
                      </span>
                    </div>
                    {order.tracking.tracking_url && (
                      <div className="flex justify-between">
                        <span>Tracking URL:</span>
                        <a
                          href={order.tracking.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          Track Order
                        </a>
                      </div>
                    )}
                    {order.tracking.shipped_at && (
                      <div className="flex justify-between">
                        <span>Shipped At:</span>
                        <span>
                          {new Date(order.tracking.shipped_at).toLocaleString(
                            "en-IN"
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* EMAIL ACTIVITY - Enhanced to show history */}
            <div className="bg-white border rounded-xl p-5">
              <h3 className="font-semibold mb-4">Email Activity History</h3>

              <div className="space-y-3">
                {getEmailHistory().map((status) => (
                  <div
                    key={status}
                    className="border rounded-lg px-4 py-3 text-sm"
                  >
                    <p className="font-medium">{EMAIL_MAP[status]}</p>
                    <p className="text-xs text-gray-500">
                      Sent automatically on status update
                    </p>

                    <button
                      onClick={() => setPreviewEmail(status)}
                      className="text-blue-600 text-xs hover:underline mt-1"
                    >
                      View email
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ================= RIGHT ================= */}
          <div className="space-y-6 lg:sticky lg:top-6 self-start">
            {/* STATUS CONTROL */}
            <div className="bg-white border rounded-xl p-5">
              <h3 className="font-semibold mb-3">Order Status Control</h3>

              {/* COD → MARK AS PAID */}
              {order.paymentMethod === "COD" &&
                order.paymentStatus !== "paid" && (
                  <button
                    onClick={handleMarkPaid}
                    disabled={loading}
                    className="w-full mb-3 py-2 rounded-md text-sm font-semibold bg-green-600 text-white hover:bg-green-700"
                  >
                    💰 Mark as Paid (COD)
                  </button>
                )}

              <div className="space-y-2">
                {STATUS_FLOW.map((status) => {
                  const type = getStatusType(status);

                  return (
                    <button
                      key={status}
                      disabled={type !== "next" || loading}
                      onClick={() => handleStatusUpdate(status)}
                      className={`w-full py-2 rounded-md border text-sm font-semibold ${STATUS_STYLE[type]}`}
                    >
                      {STATUS_LABEL[status]}
                      {type === "active" && " (Current)"}
                      {type === "completed" && " ✓"}
                    </button>
                  );
                })}
              </div>

              {/* GUIDE */}
              <div className="mt-4 bg-gray-50 border rounded-lg p-3 text-xs">
                <ul className="space-y-1">
                  <li>🟡 Current stage</li>
                  <li>🟢 Completed</li>
                  <li>🔵 Next action</li>
                  <li>⚪ Locked</li>
                  <li>🔴 Cancelled (final)</li>
                </ul>
              </div>

              {/* IMPORTANT */}
              <div className="mt-4 bg-amber-50 border border-amber-200 p-4 rounded text-xs">
                <ul className="list-disc ml-4 space-y-1">
                  <li>Status must be updated step-by-step</li>
                  <li>Qikink push ke baad cancellation allowed nahi</li>
                  <li>Delivered / Cancelled final state</li>
                  <li>Email automatically customer ko jaata hai</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* EMAIL PREVIEW */}
        {previewEmail && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-[90%] max-w-xl p-6">
              <h3 className="font-semibold mb-2">{EMAIL_MAP[previewEmail]}</h3>

              <div className="border rounded-lg p-4 text-sm">
                Hello {order.shippingAddress.name},
                <br />
                Your order <b>#{order.order_id}</b> is currently{" "}
                <b>{STATUS_LABEL[previewEmail]}</b>.
              </div>

              <div className="text-right mt-4">
                <button
                  onClick={() => setPreviewEmail(null)}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🚚 SHIPPING DETAILS MODAL */}
        {showShippingForm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl w-[90%] max-w-md p-6">
              <h3 className="font-semibold mb-4">Shipping Details</h3>

              {/* Tracking ID */}
              <input
                type="text"
                placeholder="Tracking ID"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm mb-3"
              />

              {/* Courier Dropdown */}
              <select
                value={courier}
                onChange={(e) => setCourier(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm mb-3"
              >
                {COURIERS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>

              {/* Tracking URL (optional) */}
              <input
                type="text"
                placeholder="Tracking URL (optional)"
                value={trackingUrl}
                onChange={(e) => setTrackingUrl(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm mb-4"
              />

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowShippingForm(false)}
                  className="text-sm px-4 py-2 border rounded-md"
                >
                  Cancel
                </button>

                <button
                  onClick={handleShippingSubmit}
                  disabled={loading}
                  className="text-sm px-4 py-2 bg-black text-white rounded-md"
                >
                  Confirm Shipment
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MasterLayout>
  );
}
